import { useMemo } from "react";

import { ApiErrorCode, getApiErrorCode, useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useQueryClient } from "@tanstack/react-query";

import { DutyScheduleDetailResponse, RecalculateDutyScheduleRequest, dutySchedulesApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { DutyScheduleFormValues } from "@/pages/duty-schedule/DutySchedule.types";
import { dutyScheduleValidationSchema } from "@/pages/duty-schedule/components/create-dialog/DutyScheduleForm.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

/**
 * The edit dialog's "Neu berechnen" (recalculate). Saves name and period and recalculates in one
 * step — there is no way to save the one without the other.
 */
export function useDutyScheduleRecalculation(dutySchedule: DutyScheduleDetailResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const validationSchema = useMemo(() => dutyScheduleValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<DutyScheduleFormValues>({
    initialValues: {
      name: dutySchedule.name,
      periodStart: dutySchedule.periodStart,
      periodEnd: dutySchedule.periodEnd,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async ({ name, periodStart, periodEnd }) => {
      // Narrowing only — the schema already requires both dates.
      if (periodStart == null || periodEnd == null) {
        return;
      }

      const request: RecalculateDutyScheduleRequest = { name, periodStart, periodEnd };

      try {
        // Handled locally so the overlap rejection is reported in German rather than as the
        // backend's English ProblemDetails.detail.
        await dutySchedulesApi.recalculateDutySchedule(
          { id: dutySchedule.id, recalculateDutyScheduleRequest: request },
          withLocalErrorHandling
        );
      } catch (error) {
        const errorCode = await getApiErrorCode(error);
        if (errorCode === ApiErrorCode.DutySchedulePeriodOverlaps) {
          showError(t("dutySchedule.create.validation.periodOverlaps"));
        } else if (errorCode === ApiErrorCode.DutyScheduleNotEditable) {
          showError(t("dutySchedule.edit.validation.notEditable"));
        } else {
          showError(t("common.requestError"));
        }
        return;
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.DutySchedules] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.DutyScheduleDetail] });
      // The Verteilung AW/BG tab's rows and Kennzahlen are computed backend-side off the just-recalculated slots.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.DutyScheduleDistributionSummary] });
      showSuccess(t("dutySchedule.edit.success"));
      onClose();
    },
  });

  return { formik };
}
